import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Badge, Button, Container, Form, ProgressBar, Spinner, Table,
} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FaPlus } from 'react-icons/fa';
import { getAllReviewSets } from '@/api/reviewSetApi';
import type { AdminReviewSetSummaryDto } from '@/types/reviews';
import './AdminReviews.scss';

const AdminAllReviewSets: React.FC = () => {
  const [sets, setSets] = useState<AdminReviewSetSummaryDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'open' | 'completed' | 'all'>('open');

  useEffect(() => {
    (async () => {
      try {
        const data = await getAllReviewSets();
        setSets(data);
      } catch (err: any) {
        setError(err?.response?.data?.error ?? err?.message ?? 'Failed to load review sets.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    return sets.filter((s) => {
      const matchStatus =
        statusFilter === 'all' ? true :
        statusFilter === 'open' ? !s.isComplete :
        s.isComplete;

      const q = search.toLowerCase();
      const matchSearch =
        !q ||
        s.setInstanceName.toLowerCase().includes(q) ||
        s.setTemplateName.toLowerCase().includes(q) ||
        s.owners.some((o) => o.ownerEmail.toLowerCase().includes(q));

      return matchStatus && matchSearch;
    });
  }, [sets, statusFilter, search]);

  const formatDate = (d?: string) =>
    d ? new Date(d).toLocaleDateString() : '—';

  if (loading) {
    return (
      <Container className="mt-4 text-center">
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <Container fluid className="mt-4 admin-reviews-page">
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h4 className="mb-0">All Review Sets</h4>
        <div className="d-flex gap-2">
          <Link to="/admin/review-sets/templates">
            <Button variant="outline-secondary" size="sm">Manage Templates</Button>
          </Link>
          <Link to="/admin/review-sets/assign">
            <Button variant="primary" size="sm">
              <FaPlus className="me-1" />
              Assign Review Set
            </Button>
          </Link>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <div className="d-flex gap-2 mb-3 flex-wrap">
        <Form.Control
          size="sm"
          style={{ maxWidth: 280 }}
          placeholder="Search by name or owner…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Form.Select
          size="sm"
          style={{ maxWidth: 160 }}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as any)}
        >
          <option value="open">Open</option>
          <option value="completed">Completed</option>
          <option value="all">All</option>
        </Form.Select>
      </div>

      <Table hover size="sm" responsive>
        <thead>
          <tr>
            <th>Name</th>
            <th>Template</th>
            <th>Category</th>
            <th>Owners</th>
            <th>Progress</th>
            <th>Due</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {filtered.length === 0 && (
            <tr>
              <td colSpan={7} className="text-center text-secondary py-4 fst-italic">
                No review sets found.
              </td>
            </tr>
          )}
          {filtered.map((s) => {
            const primaryOwner = s.owners.find((o) => o.isPrimary);
            const otherOwners = s.owners.filter((o) => !o.isPrimary);
            return (
              <tr key={s.setInstanceGuid}>
                <td>
                  <strong>{s.setInstanceName}</strong>
                  <div className="text-secondary small">Assigned by {s.assignedBy}</div>
                </td>
                <td className="small">{s.setTemplateName}</td>
                <td>
                  <Badge bg="secondary">{s.setTemplateCategory}</Badge>
                </td>
                <td className="small">
                  {primaryOwner && (
                    <span className="d-block">
                      <Badge bg="info" text="dark" className="me-1">Primary</Badge>
                      {primaryOwner.ownerEmail}
                    </span>
                  )}
                  {otherOwners.map((o) => (
                    <span key={o.ownerEmail} className="d-block text-secondary">{o.ownerEmail}</span>
                  ))}
                </td>
                <td style={{ minWidth: 120 }}>
                  <ProgressBar
                    now={s.progressPercent}
                    label={`${s.completedReviews}/${s.totalReviews}`}
                    variant={s.isComplete ? 'success' : s.progressPercent > 50 ? 'info' : 'primary'}
                    style={{ height: 18 }}
                  />
                </td>
                <td className="small text-nowrap">{formatDate(s.dueDate)}</td>
                <td>
                  {s.isComplete ? (
                    <Badge bg="success">Complete</Badge>
                  ) : (
                    <Badge bg="warning" text="dark">In Progress</Badge>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </Container>
  );
};

export default AdminAllReviewSets;
